// src/pages/admin/Education/CreateScholarship.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaSpinner,
  FaTag,
  FaBuilding,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaGlobe,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaCheckCircle,
  FaStar,
} from 'react-icons/fa';
import { successToast, errorToast } from '../../Utils/toast';
import { useCreateScholarshipMutation } from '../../redux/services/scholarshipsService/scholarships.Service';

function CreateScholarship() {
  const navigate = useNavigate();
  const [createScholarship, { isLoading }] = useCreateScholarshipMutation();

  const [formData, setFormData] = useState({
    title: '',
    category: 'scholarship',
    type: 'government',
    description: '',
    eligibility_criteria: '',
    benefits: '',
    documents_required: '',
    application_process: '',
    amount: '',
    deadline: '',
    website: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    is_featured: false,
    is_verified: false,
    status: 'pending',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      errorToast('Title is required');
      return;
    }

    if (!formData.description.trim()) {
      errorToast('Description is required');
      return;
    }

    try {
      const response = await createScholarship(formData).unwrap();
      successToast(response?.message || 'Scholarship created successfully');
      navigate('/admin/education');
    } catch (error) {
      console.error('Create error:', error);
      errorToast(error?.data?.message || 'Failed to create scholarship');
    }
  };

  return (
    <div>
      {/* HEADER */}
      <div className="page-header-breadcrumb all_breadcrumb">
        <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
          <nav>
            <h4 className="fw-semibold">
              <Link to="/admin/education" className="text-primary me-2">
                <FaArrowLeft />
              </Link>
              Create Scholarship
            </h4>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item active">
                <Link to="/admin">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/admin/education">Education</Link>
              </li>
              <li className="breadcrumb-item">Create Scholarship</li>
            </ol>
          </nav>
        </div>
        <div className="page-title fw-semibold fs-18 mb-0">
          <button
            className="btn btn-secondary me-2"
            onClick={() => navigate('/admin/education')}
          >
            <FaTimes className="me-1" /> Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FaSpinner className="spinning me-2" />
                Creating...
              </>
            ) : (
              <>
                <FaSave className="me-1" /> Create Scholarship
              </>
            )}
          </button>
        </div>
      </div>

      {/* FORM */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Basic Information */}
              <div className="col-12">
                <h5 className="fw-bold mb-3">📋 Basic Information</h5>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>
                    Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter scholarship title"
                    required
                  />
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>
                    Category <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="scholarship">Scholarship</option>
                    <option value="education_loan">Education Loan</option>
                    <option value="career_guidance">Career Guidance</option>
                    <option value="skill_development">Skill Development</option>
                    <option value="study_abroad">Study Abroad</option>
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>
                    Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                  >
                    <option value="government">Government</option>
                    <option value="private">Private</option>
                    <option value="trust">Trust</option>
                    <option value="corporate">Corporate</option>
                    <option value="international">International</option>
                  </select>
                </div>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>
                    Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter detailed description"
                    rows="4"
                    required
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Amount</label>
                  <input
                    type="text"
                    className="form-control"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="e.g., ₹50,000 per year"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Deadline</label>
                  <input
                    type="date"
                    className="form-control"
                    name="deadline"
                    value={formData.deadline}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Website</label>
                  <input
                    type="url"
                    className="form-control"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              {/* Eligibility Criteria */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">✅ Eligibility Criteria</h5>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Eligibility Criteria</label>
                  <textarea
                    className="form-control"
                    name="eligibility_criteria"
                    value={formData.eligibility_criteria}
                    onChange={handleChange}
                    placeholder="Enter eligibility criteria (one per line)"
                    rows="3"
                  />
                </div>
              </div>

              {/* Benefits */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">🎯 Benefits</h5>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Benefits</label>
                  <textarea
                    className="form-control"
                    name="benefits"
                    value={formData.benefits}
                    onChange={handleChange}
                    placeholder="Enter benefits (one per line)"
                    rows="3"
                  />
                </div>
              </div>

              {/* Documents Required */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">📄 Documents Required</h5>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Documents Required</label>
                  <textarea
                    className="form-control"
                    name="documents_required"
                    value={formData.documents_required}
                    onChange={handleChange}
                    placeholder="Enter required documents (one per line)"
                    rows="3"
                  />
                </div>
              </div>

              {/* Application Process */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">📝 Application Process</h5>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Application Process</label>
                  <textarea
                    className="form-control"
                    name="application_process"
                    value={formData.application_process}
                    onChange={handleChange}
                    placeholder="Enter application process steps (one per line)"
                    rows="3"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">📞 Contact Information</h5>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contact@example.com"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Address</label>
                  <input
                    type="text"
                    className="form-control"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter address"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>City</label>
                  <input
                    type="text"
                    className="form-control"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter city"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>State</label>
                  <input
                    type="text"
                    className="form-control"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="Enter state"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Pincode</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="Enter pincode"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">⚙️ Options</h5>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Status</label>
                  <select
                    className="form-control"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <div className="form-check mt-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="is_featured"
                      checked={formData.is_featured}
                      onChange={handleChange}
                      id="is_featured"
                    />
                    <label className="form-check-label" htmlFor="is_featured">
                      <FaStar className="text-warning me-1" /> Featured Scholarship
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <div className="form-check mt-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="is_verified"
                      checked={formData.is_verified}
                      onChange={handleChange}
                      id="is_verified"
                    />
                    <label className="form-check-label" htmlFor="is_verified">
                      <FaCheckCircle className="text-success me-1" /> Verified Scholarship
                    </label>
                  </div>
                </div>
              </div>

              {/* Meta Information */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">🔍 SEO / Meta Information</h5>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Meta Title</label>
                  <input
                    type="text"
                    className="form-control"
                    name="meta_title"
                    value={formData.meta_title}
                    onChange={handleChange}
                    placeholder="Meta title for SEO"
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Meta Description</label>
                  <textarea
                    className="form-control"
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={handleChange}
                    placeholder="Meta description for SEO"
                    rows="2"
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Meta Keywords</label>
                  <input
                    type="text"
                    className="form-control"
                    name="meta_keywords"
                    value={formData.meta_keywords}
                    onChange={handleChange}
                    placeholder="Comma separated keywords"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="col-12 mt-4">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="spinning me-2" />
                      Creating Scholarship...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Create Scholarship
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateScholarship;